import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { marked } from "marked";

// The twelve legal documents, in publication order. `key` becomes the URL
// hash slug (#legal/privacy) and the file name under /public/legal/.
export const LEGAL_DOCS = [
  { key: "privacy",       title: "Privacy Policy",              file: "01-privacy-policy.md",              foot: true  },
  { key: "terms",         title: "Terms of Service",            file: "02-terms-of-service.md",            foot: true  },
  { key: "refund",        title: "Refund & Cancellation",       file: "03-refund-cancellation-policy.md",  foot: true  },
  { key: "payment",       title: "Payment & Billing",           file: "04-payment-billing-terms.md",       foot: false },
  { key: "delivery",      title: "Service Delivery",            file: "05-service-delivery-policy.md",     foot: true  },
  { key: "revisions",     title: "Revision Policy",             file: "06-revision-policy.md",             foot: false },
  { key: "content",       title: "Content Rights & Licensing",  file: "07-content-rights-licensing.md",    foot: false },
  { key: "cookies",       title: "Cookie Policy",               file: "08-cookie-policy.md",               foot: true  },
  { key: "disclaimer",    title: "Disclaimer",                  file: "09-disclaimer.md",                  foot: true  },
  { key: "acceptable",    title: "Acceptable Use",              file: "10-acceptable-use.md",              foot: false },
  { key: "communications",title: "Communications Consent",      file: "11-communications-consent.md",      foot: false },
  { key: "grievance",     title: "Contact & Grievance",         file: "12-contact-grievance.md",           foot: false },
];

const LegalCtx = createContext({ open: (_k) => {}, close: () => {}, current: null });

export const useLegal = () => useContext(LegalCtx);

export function LegalProvider({ children }) {
  const [current, setCurrent] = useState(null);

  const open = useCallback((key) => {
    if (!LEGAL_DOCS.find((d) => d.key === key)) return;
    setCurrent(key);
    if (typeof window !== "undefined") {
      window.history.replaceState(null, "", `#legal/${key}`);
      document.body.style.overflow = "hidden";
    }
  }, []);

  const close = useCallback(() => {
    setCurrent(null);
    if (typeof window !== "undefined") {
      if (window.location.hash.startsWith("#legal/")) {
        window.history.replaceState(null, "", window.location.pathname);
      }
      document.body.style.overflow = "";
    }
  }, []);

  // Deep-link support — open the reader when the URL hash matches on load
  // or on navigation.
  useEffect(() => {
    const applyHash = () => {
      const m = window.location.hash.match(/^#legal\/([a-z-]+)$/);
      if (m) open(m[1]);
    };
    applyHash();
    window.addEventListener("hashchange", applyHash);
    return () => window.removeEventListener("hashchange", applyHash);
  }, [open]);

  useEffect(() => {
    if (!current) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [current, close]);

  return (
    <LegalCtx.Provider value={{ open, close, current }}>
      {children}
      <AnimatePresence>
        {current && <LegalReader activeKey={current} onSelect={open} onClose={close} />}
      </AnimatePresence>
    </LegalCtx.Provider>
  );
}

function LegalReader({ activeKey, onSelect, onClose }) {
  const [html, setHtml] = useState("");
  const [loading, setLoading] = useState(true);
  const doc = LEGAL_DOCS.find((d) => d.key === activeKey);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setHtml("");
    fetch(`/legal/${doc.file}`)
      .then((r) => (r.ok ? r.text() : Promise.reject(new Error(String(r.status)))))
      .then((md) => !cancelled && setHtml(marked.parse(md, { async: false })))
      .catch(() => !cancelled && setHtml("<p>Unable to load this document. Try again shortly.</p>"))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [doc.file]);

  // Scroll to top of the pane whenever the doc changes.
  useEffect(() => {
    const pane = document.getElementById("legal-scroll");
    if (pane) pane.scrollTo({ top: 0 });
  }, [activeKey]);

  return (
    <motion.div
      className="fixed inset-0 z-[100] flex items-stretch"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25 }}
      role="dialog"
      aria-modal="true"
      aria-label={doc.title}
      data-testid="legal-reader"
    >
      {/* Backdrop */}
      <button
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-ink/80 backdrop-blur-md"
        data-testid="legal-backdrop"
      />

      {/* Panel */}
      <motion.div
        className="relative m-auto flex h-[92vh] w-[min(1180px,94vw)] flex-col overflow-hidden rounded-[2px] border border-hairline bg-surface shadow-[0_40px_120px_-40px_rgba(0,0,0,0.6)] sm:flex-row"
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 20, opacity: 0 }}
        transition={{ duration: 0.35, ease: [0.22, 0.61, 0.36, 1] }}
      >
        {/* Sidebar */}
        <aside className="hidden shrink-0 border-r border-hairline bg-ground/60 sm:block sm:w-64">
          <div className="p-6">
            <div className="eyebrow">Policies</div>
            <p className="mt-3 text-xs italic leading-relaxed text-mutedText/80">
              Placeholders in square brackets are pending final review with counsel.
            </p>
          </div>
          <nav className="max-h-[calc(92vh-140px)] overflow-y-auto pb-6">
            {LEGAL_DOCS.map((d, i) => {
              const isActive = d.key === activeKey;
              return (
                <button
                  key={d.key}
                  onClick={() => onSelect(d.key)}
                  className={`flex w-full items-center gap-3 border-l-2 px-6 py-2.5 text-left text-sm transition-colors duration-300 ${
                    isActive
                      ? "border-gold bg-ground/80 text-primaryText"
                      : "border-transparent text-mutedText hover:border-gold/40 hover:text-primaryText"
                  }`}
                  data-testid={`legal-nav-${d.key}`}
                >
                  <span className="font-mono text-[10px] tracking-[0.2em] text-gold">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="leading-tight">{d.title}</span>
                </button>
              );
            })}
          </nav>
        </aside>

        {/* Reader pane */}
        <div className="flex min-w-0 flex-1 flex-col">
          <header className="flex items-center justify-between border-b border-hairline px-6 py-5 sm:px-10">
            <div className="min-w-0">
              <div className="eyebrow">Legal</div>
              <h2 className="display mt-2 truncate text-2xl text-primaryText sm:text-3xl" style={{ fontWeight: 300 }}>
                {doc.title}
              </h2>
            </div>
            <button
              onClick={onClose}
              className="glide flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-hairline text-mutedText hover:border-gold/60 hover:text-primaryText"
              aria-label="Close policies"
              data-testid="legal-close"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M2 2l12 12M14 2L2 14" stroke="currentColor" strokeWidth="1.2" />
              </svg>
            </button>
          </header>

          {/* Mobile picker */}
          <div className="border-b border-hairline px-6 py-3 sm:hidden">
            <select
              value={activeKey}
              onChange={(e) => onSelect(e.target.value)}
              className="w-full rounded-[2px] border border-hairline bg-ground/60 px-3 py-2 text-sm text-primaryText"
              data-testid="legal-mobile-select"
            >
              {LEGAL_DOCS.map((d, i) => (
                <option key={d.key} value={d.key}>
                  {String(i + 1).padStart(2, "0")} · {d.title}
                </option>
              ))}
            </select>
          </div>

          <div
            id="legal-scroll"
            className="prose-legal flex-1 overflow-y-auto px-6 py-8 sm:px-10 sm:py-10"
            data-testid="legal-content"
          >
            {loading ? (
              <div className="animate-pulse space-y-3">
                <div className="h-4 w-1/3 rounded bg-hairline/60" />
                <div className="h-3 w-full rounded bg-hairline/50" />
                <div className="h-3 w-11/12 rounded bg-hairline/50" />
                <div className="h-3 w-4/5 rounded bg-hairline/50" />
              </div>
            ) : (
              <div dangerouslySetInnerHTML={{ __html: html }} />
            )}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
